import { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'

import { NavBar } from '../../components/NavBar'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import {
  HiOutlineDocumentReport,
  HiOutlineShoppingBag,
  HiOutlineUsers,
  HiOutlineCurrencyDollar,
  HiOutlineCalendar,
  HiOutlineChevronRight,
  HiOutlineExclamation,
  HiOutlineTrendingDown,
  HiOutlineLightBulb,
  HiOutlineFire,
  HiOutlineShoppingCart,
  HiOutlinePaperAirplane,
  HiOutlineEye,
} from 'react-icons/hi'
import { FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { api } from '../../api/api'
import { maskCurrency, maskPercentage, maskPhone } from '../../utils/utils'
import { suggestionReviewPath } from './suggestion-navigation'
import { Toast } from '../../utils/Toast'
import Swal from 'sweetalert2'
import { TabReport } from '../Report/components/TabReport'
import { AbandonedCartDetails } from './components/AbandonedCartDetails'
import { useNavigate } from 'react-router-dom'
import { useContext } from 'react'
import { GlobalContext } from '../../context/GlobalContext'
import { useCompany } from '../../hooks/useCompany'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

const COLORS = {
  bg: '#f5f6fa',
  card: '#1a1f3a',
  cardHover: '#1f2545',
  orange: '#ff6200',
  orangeDark: '#d95700',
  text: '#e8eaf6',
  textMuted: '#8892b0',
  border: '#2a2f52',
  green: '#4caf50',
  red: '#f44336',
  yellow: '#ffc107',
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      labels: { color: COLORS.textMuted, usePointStyle: true, pointStyleWidth: 8, font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: '#1a1f3a',
      borderColor: COLORS.border,
      borderWidth: 1,
      titleColor: COLORS.text,
      bodyColor: COLORS.textMuted,
    },
  },
  scales: {
    x: {
      ticks: { color: COLORS.textMuted, maxTicksLimit: 10, font: { size: 11 } },
      grid: { color: 'rgba(255,255,255,0.04)' },
      border: { display: false },
    },
    y: {
      position: 'left',
      ticks: {
        color: COLORS.textMuted,
        font: { size: 11 },
        callback: (v) => `R$ ${(v / 1).toLocaleString('pt-BR')}`,
      },
      grid: { color: 'rgba(255,255,255,0.04)' },
      border: { display: false },
    },
    yOrders: {
      position: 'right',
      ticks: {
        color: COLORS.textMuted,
        font: { size: 11 },
        precision: 0,
      },
      grid: { drawOnChartArea: false },
      border: { display: false },
    },
  },
}

function KpiCard({ icon: Icon, iconBg, label, value, change, positive }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: '16px 18px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      minWidth: 220
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)', margin: 0 }}>{label}</p>
          <p style={{ color: COLORS.text, fontSize: 26, fontWeight: 700, margin: '6px 0 0' }}>{value}</p>
        </div>
        <div style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={22} color="#fff" />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {positive
          ? <FiArrowUpRight size={15} color={COLORS.green} />
          : <FiArrowDownRight size={15} color={COLORS.red} />}
        <span style={{ color: positive ? COLORS.green : COLORS.red, fontSize: 'var(--font-size-caption)', fontWeight: 600 }}>{change}</span>
        <span style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)' }}>vs. mês anterior</span>
      </div>
    </div>
  )
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  return width
}

const PT_MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const formatMonth = (dt) => `${PT_MONTHS[dt.month - 1]} de ${dt.year}`

const emptyReportingOrders = () => ({
  totalSales: { value: 0, previousValue: 0, percentage: 0 },
  countOrders: { value: 0, previousValue: 0, percentage: 0 },
  avgTicket: { value: 0, previousValue: 0, percentage: 0 },
  salesByDay: [],
})

const emptyReportingMetrics = () => ({
  countAccessesMenu: { value: 0, previousValue: 0, percentage: 0 },
  metric: { value: 0, previousValue: 0, percentage: 0 },
  customers: { records: [], value: 0, previousValue: 0, percentage: 0 },
  countAbandonedCarts: { abandonedCount: 0, lostValue: 0, topProducts: [], customers: [] },
  topSellingProducts: [],
})

function suggestionConfirmationText(suggestion) {
  const data = suggestion?.data && typeof suggestion.data === 'object' ? suggestion.data : {}
  const actionLabels = {
    cupom_desconto: 'Criar cupom inativo para revisão',
    campanha_capturados: 'Criar campanha para contatos capturados',
    campanha_inativos: 'Criar campanha para clientes inativos',
    produto_alto_abandono: 'Abrir cadastro para revisar o produto',
    campanha_aumentar_vendas: 'Criar campanha para aumentar vendas',
  }
  const audience = data.audienceCount ?? data.inactiveCount ?? data.capturedCount ?? data.customers?.length
  const discount = data.suggestedDiscount ?? data.discountPercentage ?? data.discount ?? data.discountValue
  const details = [`Ação: ${actionLabels[suggestion?.type] || String(suggestion?.type || 'não informada').replaceAll('_', ' ')}`]

  if (audience !== undefined) details.push(`Público estimado: ${audience}`)
  if (discount !== undefined) details.push(`Desconto: ${discount}%`)
  if (data.productName) details.push(`Produto: ${data.productName}`)

  details.push(suggestion?.type === 'produto_alto_abandono'
    ? 'O cadastro será aberto sem alterações. Os dados de abandono não informam o motivo da desistência.'
    : suggestion?.type === 'cupom_desconto'
      ? 'O cupom será criado inativo. Revise o desconto e ative quando desejar. Nenhuma mensagem será enviada.'
      : 'A campanha será salva como rascunho. Revise a mensagem, os descontos sugeridos e o público antes de agendar. Nenhuma mensagem será enviada agora.')

  return details.join('\n')
}

export function ReportsV2() {
  const navigate = useNavigate()
  const { isAccess } = useContext(GlobalContext)
  const { company } = useCompany()
  const [reportingOrders, setReportingOrders] = useState(emptyReportingOrders)
  const [reportingMetrics, setReportingMetrics] = useState(emptyReportingMetrics)

  const [suggestions, setSuggestions] = useState([])
  const [executingSuggestion, setExecutingSuggestion] = useState(null)
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [dashboardErrors, setDashboardErrors] = useState([])
  const [dashboardRetry, setDashboardRetry] = useState(0)
  const dashboardRequest = useRef(0)
  const [selectedAbandonedCart, setSelectedAbandonedCart] = useState(null)

  async function sendMessageToCustomer(customer) {
    const customerName = (customer?.name || customer?.customerName)?.trim() || 'cliente'
    const result = await Swal.fire({
      title: `Enviar mensagem para ${customerName}`,
      input: 'textarea',
      inputLabel: maskPhone(customer?.phone),
      inputValue: `Olá, ${customerName}! Tudo bem?`,
      inputAttributes: {
        'aria-label': `Mensagem para ${customerName}`,
      },
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Enviar mensagem',
      confirmButtonColor: COLORS.orange,
      showLoaderOnConfirm: true,
      inputValidator: (value) => {
        if (!value?.trim()) return 'Digite uma mensagem antes de enviar.'
      },
      preConfirm: async (message) => {
        try {
          await api.post('/integration-whatsapp/send/message', {
            phone: customer.phone,
            message: message.trim(),
          })
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message || 'Não foi possível enviar a mensagem. Verifique a conexão do WhatsApp.'
          )
          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    })

    if (result.isConfirmed) {
      Toast.success('Mensagem enviada com sucesso.', 2500)
    }
  }

  const [selectedMonth, setSelectedMonth] = useState(DateTime.local().startOf('month'))
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false)
  const monthOptions = Array.from({ length: 6 }, (_, i) => DateTime.local().startOf('month').minus({ months: i }))

  const width = useWindowWidth()
  const isMobile = width < 640
  const isTablet = width < 1024

  const s = {
    page: {
      background: COLORS.bg,
      minHeight: '100vh',
      color: COLORS.text,
      fontFamily: 'inherit',
      padding: isMobile ? '16px 12px' : '24px 28px',
      boxSizing: 'border-box',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 24,
      flexWrap: 'wrap',
      gap: 12,
    },
    card: {
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: 20,
    },
    sectionTitle: {
      color: COLORS.text,
      fontSize: 15,
      fontWeight: 600,
      margin: '0 0 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    badge: (positive) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      background: positive ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.15)',
      color: positive ? COLORS.green : COLORS.red,
      borderRadius: 20,
      padding: '2px 8px',
      fontSize: 12,
      fontWeight: 600,
    }),
    btn: (active) => ({
      background: active ? COLORS.orange : 'transparent',
      color: active ? '#fff' : COLORS.textMuted,
      border: `1px solid ${active ? COLORS.orange : COLORS.border}`,
      borderRadius: 8,
      padding: '5px 12px',
      fontSize: 12,
      cursor: 'pointer',
      fontWeight: active ? 600 : 400,
      transition: 'all 0.2s',
    }),
    alertCard: (color) => ({
      background: `${color}18`,
      border: `1px solid ${color}44`,
      borderRadius: 10,
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      cursor: 'default',
    }),
    divider: {
      height: 1,
      background: COLORS.border,
      margin: '10px 0',
    },
    rankDot: (color) => ({
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
    }),
    bottomCard: {
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: '16px 20px',
      flex: isMobile ? '1 1 100%' : '1',
      minWidth: isMobile ? '100%' : 180,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    },
    bottomIcon: (bg) => ({
      width: 52,
      height: 52,
      borderRadius: 12,
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }),
  }

  useEffect(() => {
    const requestId = ++dashboardRequest.current
    const startDate = selectedMonth.toSQLDate()
    const isCurrentMonth = selectedMonth.hasSame(DateTime.local(), 'month')
    const endDate = isCurrentMonth ? DateTime.local().toSQLDate() : selectedMonth.endOf('month').toSQLDate()

    setDashboardLoading(true)
    setDashboardErrors([])
    setReportingOrders(emptyReportingOrders())
    setReportingMetrics(emptyReportingMetrics())
    setSuggestions([])
    setSelectedAbandonedCart(null)

    Promise.allSettled([
      api.get('/orders/reporting', { params: { startDate, endDate } }),
      api.get('/metrics/count', { params: { startDate, endDate } }),
      api.get('/metrics/suggestions', { params: { startDate, endDate } }),
    ]).then(([ordersResult, metricsResult, suggestionsResult]) => {
      if (requestId !== dashboardRequest.current) return

      const errors = []
      if (ordersResult.status === 'fulfilled') setReportingOrders(ordersResult.value.data)
      else errors.push('pedidos e vendas')

      if (metricsResult.status === 'fulfilled') setReportingMetrics(metricsResult.value.data)
      else errors.push('métricas')

      if (suggestionsResult.status === 'fulfilled') setSuggestions(suggestionsResult.value.data)
      else errors.push('sugestões')

      setDashboardErrors(errors)
      setDashboardLoading(false)
    })

    return () => {
      if (dashboardRequest.current === requestId) dashboardRequest.current += 1
    }
  }, [selectedMonth, dashboardRetry])


  const chartData = {
    labels: reportingOrders.salesByDay.map(d => DateTime.fromISO(d.date).toFormat('dd/MM')),
    datasets: [
      {
        label: 'Vendas (R$)',
        data: reportingOrders.salesByDay.map(d => d.total),
        borderColor: COLORS.orange,
        backgroundColor: 'rgba(255, 98, 0, 0.08)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: COLORS.orange,
        borderWidth: 2,
      },
      {
        label: 'Pedidos',
        data: reportingOrders.salesByDay.map(d => d.count),
        borderColor: '#7c6aff',
        backgroundColor: 'rgba(124, 106, 255, 0.06)',
        tension: 0.4,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: '#7c6aff',
        borderWidth: 2,
        yAxisID: 'yOrders',
      },
    ],
  }

  const validCapturedCustomers = reportingMetrics.customers.records.filter((customer) => {
    const phoneLength = String(customer?.phone ?? '').replace(/\D/g, '').length
    return phoneLength === 10 || phoneLength === 11
  })
  const abandonedCartCustomers = reportingMetrics.countAbandonedCarts.customers || []

  function getAbandonedCartCustomer(customer) {
    if (customer?.payload) return customer

    const phone = String(customer?.phone || '').replace(/\D/g, '')
    return abandonedCartCustomers.find((abandonedCustomer) => (
      String(abandonedCustomer?.phone || '').replace(/\D/g, '') === phone
    ))
  }

  return (
    <NavBar>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={s.page}>

        {/* Header */}
        <div className="system-page-header" style={s.header}>
          <div>
            <h1 style={{ color: '#2e2e2e', margin: 0, fontSize: 'var(--font-size-page-title)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlineDocumentReport size={32} color={COLORS.orange} />
              Relatórios
            </h1>
            <p style={{ margin: '4px 0 0', color: '#2e2e2e7a', fontSize: 'var(--font-size-page-description)' }}>
              Acompanhe o desempenho da sua loja em tempo real e tome decisões melhores.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMonthDropdownOpen(v => !v)}
                style={{
                  background: COLORS.card,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.text,
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 'var(--font-size-button)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <HiOutlineCalendar size={16} color={COLORS.orange} /> {formatMonth(selectedMonth)} ▾
              </button>
              {monthDropdownOpen && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                    onClick={() => setMonthDropdownOpen(false)}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    background: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    overflow: 'hidden',
                    zIndex: 100,
                    minWidth: 180,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  }}>
                    {monthOptions.map((m) => {
                      const isSelected = m.toFormat('yyyy-MM') === selectedMonth.toFormat('yyyy-MM')
                      return (
                        <button
                          key={m.toISO()}
                          onClick={() => { setSelectedMonth(m); setMonthDropdownOpen(false) }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '10px 16px',
                            background: isSelected ? `${COLORS.orange}22` : 'transparent',
                            color: isSelected ? COLORS.orange : COLORS.text,
                            border: 'none',
                            textAlign: 'left',
                            fontSize: 'var(--font-size-button)',
                            cursor: 'pointer',
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {formatMonth(m)}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {dashboardLoading && (
          <div className="alert alert-info">Carregando dados do relatório...</div>
        )}
        {dashboardErrors.length > 0 && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
            <span>Não foi possível carregar: {dashboardErrors.join(', ')}. Os demais dados continuam disponíveis.</span>
            <button className="btn btn-sm btn-outline-danger" onClick={() => setDashboardRetry((value) => value + 1)}>Tentar novamente</button>
          </div>
        )}

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 20, overflow: 'auto' }}>
          <KpiCard
            icon={HiOutlineCurrencyDollar}
            iconBg={`${COLORS.orange}33`}
            label="Vendas totais"
            value={maskCurrency(reportingOrders.totalSales.value)}
            // format percentage with sign and % symbol, e.g. +12.5% or -8.3%
            change={maskPercentage(reportingOrders.totalSales.percentage)}
            positive={reportingOrders.totalSales.percentage < 0 ? false : true}
          />
          <KpiCard
            icon={HiOutlineShoppingBag}
            iconBg="rgba(124,106,255,0.2)"
            label="Pedidos"
            value={reportingOrders.countOrders.value}
            change={maskPercentage(reportingOrders.countOrders.percentage)}
            positive={reportingOrders.countOrders.percentage < 0 ? false : true}
          />
          <KpiCard
            icon={HiOutlineShoppingCart}
            iconBg="rgba(76,175,80,0.2)"
            label="Ticket médio"
            value={maskCurrency(reportingOrders.avgTicket.value)}
            change={maskPercentage(reportingOrders.avgTicket.percentage)}
            positive={reportingOrders.avgTicket.percentage < 0 ? false : true}
          />
          <KpiCard
            icon={HiOutlineUsers}
            iconBg="rgba(255,193,7,0.2)"
            label="Acessos ao cardápio (únicos)"
            value={reportingMetrics.countAccessesMenu.value}
            change={maskPercentage(reportingMetrics.countAccessesMenu.percentage)}
            positive={reportingMetrics.countAccessesMenu.percentage < 0 ? false : true}
          />
        </div>

        {/* Main grid                                                      minmax(0, 1fr) 300px */}
        <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : 'minmax(0, 1fr) 300px', gap: 16, marginBottom: 16, alignItems: 'stretch' }}>

          {/* Chart */}
          <div style={{ ...s.card, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <p style={s.sectionTitle}>
                <HiOutlineFire size={17} color={COLORS.orange} /> Vendas em {formatMonth(selectedMonth)}
              </p>
            </div>
            <div style={{ flex: 1, overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
              <div style={{ minWidth: isMobile ? 500 : '100%', height: '100%', minHeight: 260, position: 'relative' }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Alerts */}
          <div style={{
            ...s.card,
          }}>
            <p style={s.sectionTitle}>
              <HiOutlineExclamation size={17} color={COLORS.orange} /> Atenções que merecem foco
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={s.alertCard(COLORS.yellow)}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.yellow}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <HiOutlineShoppingCart size={17} color={COLORS.yellow} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-body)', fontWeight: 600, color: COLORS.text }}>{reportingMetrics.countAbandonedCarts.abandonedCount} carrinhos abandonados</p>
                    <p style={{ margin: '2px 0 0', fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>{maskCurrency(reportingMetrics.countAbandonedCarts.lostValue)} em vendas perdidas</p>
                    {/* <span style={{ color: COLORS.orange, fontSize: 12, cursor: 'pointer' }}>Ver detalhes</span> */}
                  </div>
                </div>
              </div>

              {reportingOrders.avgTicket.percentage < 0 && (
                <div style={s.alertCard(COLORS.red)}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.red}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <HiOutlineTrendingDown size={17} color={COLORS.red} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-body)', fontWeight: 600, color: COLORS.text }}>Ticket médio caiu {maskPercentage(Math.abs(reportingOrders.avgTicket.percentage)).replace('+', '')}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>vs. mês anterior</p>
                    </div>
                  </div>
                </div>
              )}

            </div>

            <div style={{ ...s.divider, margin: '16px 0' }} />

            <p style={s.sectionTitle}>
              <HiOutlineLightBulb size={17} color={COLORS.orange} /> Sugestões para você
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {suggestions.map((s2, i) => (
                <div key={i} onClick={async () => {
                  if (executingSuggestion !== null) return;

                  const confirmation = await Swal.fire({
                    title: s2.type === 'produto_alto_abandono' ? 'Revisar produto?' : 'Preparar sugestão?',
                    text: suggestionConfirmationText(s2),
                    showCancelButton: true,
                    cancelButtonText: 'Cancelar',
                    confirmButtonText: s2.type === 'produto_alto_abandono' ? 'Abrir produto' : 'Gerar para revisar',
                    confirmButtonColor: COLORS.orange,
                  })

                  if (confirmation.isConfirmed) {
                    setExecutingSuggestion(i)
                    const toastId = toast.loading("Preparando sugestão para revisão...")
                    let executeResponse

                    try {
                      executeResponse = await api.post(`/metrics/suggestions/${s2.type}/execute`, {
                        data: s2.data
                      })
                    } catch (error) {
                      toast.update(toastId, {
                        render: error?.response?.data?.message || "Erro ao preparar a sugestão.",
                        type: "error",
                        isLoading: false,
                        autoClose: 4000,
                        hideProgressBar: true,
                      })
                      setExecutingSuggestion(null)
                      return
                    }

                    setSuggestions((current) => current.filter((suggestion) => suggestion !== s2))
                    toast.update(toastId, {
                      render: "Sugestão pronta para revisão!",
                      type: "success",
                      isLoading: false,
                      autoClose: 2000,
                      hideProgressBar: true,
                    })

                    setExecutingSuggestion(null)
                    const reviewPath = suggestionReviewPath(s2.type, executeResponse.data)

                    if (reviewPath) {
                      navigate(reviewPath)
                    }

                    try {
                      await api.post(`/metrics/suggestions/${s2.type}/dismiss`, {})
                    } catch (error) {
                      Toast.warning("A sugestão foi preparada, mas não foi possível marcá-la como dispensada.", 4000)
                    }


                  }

                }} style={{ cursor: executingSuggestion !== null ? 'not-allowed' : 'pointer', opacity: executingSuggestion !== null && executingSuggestion !== i ? 0.45 : 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: executingSuggestion === i ? COLORS.yellow : COLORS.orange, marginTop: 5, flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted, lineHeight: 1.5 }}>
                      {executingSuggestion === i ? 'Preparando...' : s2.description}
                    </span>
                  </div>
                  {executingSuggestion === i ? (
                    <div style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: `2px solid ${COLORS.border}`,
                      borderTopColor: COLORS.orange,
                      flexShrink: 0,
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  ) : (
                    <HiOutlineChevronRight size={14} color={COLORS.textMuted} style={{ flexShrink: 0 }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 300px', gap: 16, marginBottom: 16, alignItems: 'stretch', paddingBottom: 20   }}>

          {/* Clientes capturados */}
          <div style={s.card}>
            <p style={s.sectionTitle}><HiOutlineUsers size={17} color={COLORS.orange} /> Clientes capturados (Mês)</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 36, fontWeight: 700, color: COLORS.text }}>{reportingMetrics.customers.value}</span>
              <span style={s.badge(reportingMetrics.customers.percentage >= 0)}>
                {reportingMetrics.customers.percentage >= 0 ? <FiArrowUpRight size={12} /> : <FiArrowDownRight size={12} />}
                {maskPercentage(reportingMetrics.customers.percentage)}
              </span>
            </div>
            <div style={{ ...s.divider, margin: '14px 0' }} />
            <p style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)', margin: '0 0 10px', fontWeight: 600 }}>Clientes capturados</p>
            {validCapturedCustomers.slice(0, 10).map((customer, index) => {
              const abandonedCartCustomer = getAbandonedCartCustomer(customer)
              const customerName = customer?.name || customer?.customerName || abandonedCartCustomer?.name || 'cliente'

              return (
                <div key={customer.id || `${customer.phone}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div style={s.rankDot(customer.color)} />
                    <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>- {customerName}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>{maskPhone(customer?.phone)}</span>
                    {abandonedCartCustomer?.payload && (
                      <button
                        type="button"
                        title={`Ver carrinho abandonado de ${customerName}`}
                        aria-label={`Ver carrinho abandonado de ${customerName}`}
                        onClick={() => setSelectedAbandonedCart({ ...customer, ...abandonedCartCustomer, name: customerName })}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 7,
                          border: `1px solid ${COLORS.border}`,
                          background: 'rgba(124,106,255,0.18)',
                          color: '#8d80ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <HiOutlineEye size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      title={`Enviar mensagem para ${customerName}`}
                      aria-label={`Enviar mensagem para ${customerName}`}
                      onClick={() => sendMessageToCustomer({ ...customer, name: customerName })}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        border: `1px solid ${COLORS.border}`,
                        background: `${COLORS.orange}22`,
                        color: COLORS.orange,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <HiOutlinePaperAirplane size={15} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Carrinhos abandonados */}
          <div style={s.card}>
            <p style={s.sectionTitle}><HiOutlineShoppingCart size={17} color={COLORS.orange} /> Carrinhos abandonados</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={{ fontSize: 36, fontWeight: 700, color: COLORS.text }}>{reportingMetrics.countAbandonedCarts.abandonedCount}</span>
              <span style={{ color: COLORS.red, fontSize: 'var(--font-size-subtitle)', fontWeight: 500 }}>{maskCurrency(reportingMetrics.countAbandonedCarts.lostValue)} perdidos</span>
            </div>
            <div style={{ ...s.divider, margin: '14px 0' }} />
            <p style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)', margin: '0 0 10px', fontWeight: 600 }}>Ultimos produtos abandonados R$</p>
            {reportingMetrics.countAbandonedCarts.topProducts.slice(0, 10).map((p) => (
              <div key={p.idProduct} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={s.rankDot(p.color)} />
                  <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>- {p.name}</span>
                </div>
                <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.text, fontWeight: 500 }}>{maskCurrency(p.totalValue ?? p.value ?? 0)}</span>
              </div>
            ))}
          </div>

          {/* Produtos em destaque */}
          <div style={s.card}>
            <p style={s.sectionTitle}><HiOutlineFire size={17} color={COLORS.orange} /> Produtos em destaque</p>
            <p style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)', margin: '0 0 8px', fontWeight: 600 }}>Mais vendidos</p>
            {reportingMetrics.topSellingProducts.slice(0, 10).map((p, indexProduct) => (
              <div key={p.idProduct} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-caption)', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{indexProduct + 1}</div>
                  <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>- {p.nameProduct}</span>
                </div>
                <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.text, fontWeight: 500 }}>{p.totalQuantity}</span>
              </div>
            ))}
            <div style={{ ...s.divider, margin: '12px 0' }} />
            <p style={{ color: COLORS.textMuted, fontSize: 'var(--font-size-subtitle)', margin: '0 0 8px', fontWeight: 600 }}>Mais abandonados</p>
            {reportingMetrics.countAbandonedCarts.topProducts.slice(0, 10).map((p, indexProduct) => (
              <div key={p.idProduct} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-caption)', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{indexProduct + 1}</div>
                  <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.textMuted }}>{p.name}</span>
                </div>
                <span style={{ fontSize: 'var(--font-size-subtitle)', color: COLORS.text, fontWeight: 500 }}>{maskCurrency(p.value ?? p.totalValue ?? 0)}</span>
              </div>
            ))}
          </div>
        </div>


        <TabReport />
        {selectedAbandonedCart && (
          <AbandonedCartDetails
            customer={selectedAbandonedCart}
            onClose={() => setSelectedAbandonedCart(null)}
            canCreate={isAccess('pedidos', 'create_pedido_balcao')}
            companyLink={company?.link}
            onCreated={() => navigate('/home')}
          />
        )}
      </div>
    </NavBar >
  )
}
